import { axiosInstance } from './axios';
import type {
    IProduct,
    IProductSummary,
    ICreateProduct,
    IUpdateProduct,
    IProductWithPricing,
    IStockMovementSummary
} from '../interfaces/product';

const BASE_URL = '/Product';

export const productService = {
    // Get all products
    getAllProducts: async (includeInactive: boolean = false) => {
        const response = await axiosInstance.get<IProduct[]>(BASE_URL, {
            params: { includeInactive }
        });
        return response.data;
    },

    // Get product summaries
    getProductSummaries: async () => {
        const response = await axiosInstance.get<IProductSummary[]>(`${BASE_URL}/summaries`);
        return response.data;
    },

    // Get product by ID
    getProductById: async (id: number) => {
        const response = await axiosInstance.get<IProduct>(`${BASE_URL}/${id}`);
        return response.data;
    },

    // Get product by code
    getProductByCode: async (itemCode: string) => {
        const response = await axiosInstance.get<IProduct>(`${BASE_URL}/code/${itemCode}`);
        return response.data;
    },

    // Get product with pricing
    getProductWithPricing: async (id: number) => {
        const response = await axiosInstance.get<IProductWithPricing>(`${BASE_URL}/${id}/pricing`);
        return response.data;
    },

    // Get all products with pricing
    getAllProductsWithPricing: async () => {
        const response = await axiosInstance.get<IProductWithPricing[]>(`${BASE_URL}/with-pricing`);
        return response.data;
    },

    // Search products
    searchProducts: async (term: string) => {
        const response = await axiosInstance.get<IProduct[]>(`${BASE_URL}/search`, {
            params: { term }
        });
        return response.data;
    },

    // Create product
    createProduct: async (product: ICreateProduct) => {
        const response = await axiosInstance.post<IProduct>(BASE_URL, product);
        return response.data;
    },

    // Update product
    updateProduct: async (id: number, product: IUpdateProduct) => {
        const response = await axiosInstance.put<IProduct>(`${BASE_URL}/${id}`, product);
        return response.data;
    },

    // Delete product
    deleteProduct: async (id: number) => {
        await axiosInstance.delete(`${BASE_URL}/${id}`);
    },

    // Check if product code is unique
    checkProductCodeUnique: async (itemCode: string, excludeId?: number) => {
        const response = await axiosInstance.get<{ isUnique: boolean }>(`${BASE_URL}/check-code/${itemCode}`, {
            params: { excludeId }
        });
        return response.data.isUnique;
    },

    // Get stock movement summary for a product
    getStockMovementSummary: async (id: number) => {
        const response = await axiosInstance.get<IStockMovementSummary>(`${BASE_URL}/${id}/stock-movement`);
        return response.data;
    },

    // Get stock movement summaries for all products
    getAllStockMovementSummaries: async () => {
        const response = await axiosInstance.get<IStockMovementSummary[]>(`${BASE_URL}/stock-movement-summaries`);
        return response.data;
    },
};
